import React from 'react';
import { format } from 'date-fns';
import { CheckCircleIcon, CalendarIcon, ClockIcon, EnvelopeIcon } from '@heroicons/react/24/solid';

const BookingConfirmation = ({ booking, onNewBooking }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-gray-600 mb-8">
            Your appointment has been successfully scheduled
          </p>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Appointment Details
            </h2>

            <div className="space-y-4">
              {/* Service */}
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="w-4 h-4 text-primary-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Service</p>
                  <p className="text-base font-semibold text-gray-900">
                    {booking.serviceName}
                  </p>
                  <p className="text-sm text-gray-600">{booking.duration} minutes</p>
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 text-primary-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Date & Time</p>
                  <p className="text-base font-semibold text-gray-900">
                    {format(new Date(booking.startTime), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm text-gray-600">
                    {format(new Date(booking.startTime), 'h:mm a')} -{' '}
                    {format(new Date(booking.endTime), 'h:mm a')}
                  </p>
                </div>
              </div>

              {/* Confirmation Number */}
              {booking.confirmationCode && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-500">Confirmation Number</p>
                  <p className="text-lg font-mono font-semibold text-gray-900 mt-1">
                    {booking.confirmationCode}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Confirmation Email */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <EnvelopeIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-blue-900">
                  Confirmation email sent
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  We've sent a confirmation email to{' '}
                  <span className="font-medium">{booking.clientEmail}</span> with all the details.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="text-left mb-8">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
              What's Next?
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2">•</span>
                <span>Check your email for the confirmation details</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2">•</span>
                <span>Please arrive 10 minutes early for your appointment</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2">•</span>
                <span>If you need to cancel or reschedule, please contact us 24 hours in advance</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onNewBooking}
              className="flex-1 py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Book Another Appointment
            </button>
            <button
              onClick={() => window.print()}
              className="flex-1 py-3 px-6 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 transition-colors"
            >
              Print Confirmation
            </button>
          </div>
        </div>

        {/* Support Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Need help? Contact us at <a href="mailto:support@example.com" className="text-primary-600 hover:text-primary-700">support@example.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
